import React, { useState, useEffect } from 'react';
import { customersAPI } from '../../services/api';
import { Customer } from '../../types';
import { MagnifyingGlassIcon, UserIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';

interface CustomerPickerProps {
  companyId: string;
  selectedCustomerId?: string;
  onCustomerSelect: (customer: Customer | null) => void;
  disabled?: boolean;
  className?: string;
}

const CustomerPicker: React.FC<CustomerPickerProps> = ({
  companyId,
  selectedCustomerId,
  onCustomerSelect,
  disabled = false,
  className = '',
}) => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

  useEffect(() => {
    if (companyId) {
      loadCustomers();
    }
  }, [companyId]);

  useEffect(() => {
    if (selectedCustomerId && customers.length > 0) {
      const customer = customers.find(c => c.id === selectedCustomerId);
      setSelectedCustomer(customer || null);
    }
  }, [selectedCustomerId, customers]);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const data = await customersAPI.getByCompany(companyId);
      setCustomers(data);
    } catch (err) {
      console.error('Error loading customers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerSelect = (customer: Customer) => {
    setSelectedCustomer(customer);
    onCustomerSelect(customer);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClearSelection = () => {
    setSelectedCustomer(null);
    onCustomerSelect(null);
    setSearchTerm('');
  };

  const filteredCustomers = customers.filter(customer =>
    customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.phone?.includes(searchTerm)
  );

  const getCustomerTypeIcon = (type: string) => {
    return type === 'business' ? (
      <BuildingOfficeIcon className="h-4 w-4 text-gray-400" />
    ) : (
      <UserIcon className="h-4 w-4 text-gray-400" />
    );
  };

  return (
    <div className={`relative ${className}`}>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Customer
      </label>
      
      {/* Selected Customer Display */}
      {selectedCustomer && (
        <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  {getCustomerTypeIcon(selectedCustomer.type)}
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">{selectedCustomer.name}</p>
                <div className="flex items-center space-x-4 text-xs text-gray-500">
                  {selectedCustomer.email && <span>{selectedCustomer.email}</span>}
                  {selectedCustomer.phone && <span>{selectedCustomer.phone}</span>}
                  <span className="capitalize">{selectedCustomer.type}</span>
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={handleClearSelection}
              disabled={disabled}
              className="text-red-600 hover:text-red-800 disabled:opacity-50"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Customer Picker Button */}
      <div className="relative">
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-left focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
        >
          <span className={selectedCustomer ? 'text-gray-900' : 'text-gray-500'}>
            {selectedCustomer ? selectedCustomer.name : 'Select a customer...'}
          </span>
          <svg className="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>

        {/* Dropdown */}
        {isOpen && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
            {/* Search Input */}
            <div className="p-3 border-b border-gray-200">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search customers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            {/* Customer List */}
            <div className="py-1">
              {loading ? (
                <div className="px-3 py-2 text-sm text-gray-500">Loading customers...</div>
              ) : filteredCustomers.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500">
                  {searchTerm ? 'No customers found matching your search.' : 'No customers available.'}
                </div>
              ) : (
                filteredCustomers.map((customer) => (
                  <button
                    key={customer.id}
                    type="button"
                    onClick={() => handleCustomerSelect(customer)}
                    className="w-full px-3 py-2 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0">
                        <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center">
                          {getCustomerTypeIcon(customer.type)}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {customer.name}
                        </p>
                        <div className="flex items-center space-x-2 text-xs text-gray-500">
                          {customer.email && <span className="truncate">{customer.email}</span>}
                          {customer.phone && <span>{customer.phone}</span>}
                          <span className="capitalize">{customer.type}</span>
                        </div>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Click outside to close */}
      {isOpen && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default CustomerPicker;
